import React from "react";
import styled from "styled-components";
import { types } from "react-bricks/frontend";
import { tablet } from "components/templates/Breakpoints";
import { color } from "styles";
import { QuoteSlide } from "components/molecules/QuoteSlide";
import { QuoteSlideInfo } from "components/molecules/QuoteSlideInfo";

export interface TestimonialItemProps {
  avatar?: types.IImageSource;
  bigImg?: types.IImageSource;
  logo?: types.IImageSource;
  quote: string;
  source?: string;
  vimeoId?: string;
  videoURL?: string;
  caseStudyURL?: string;
  fontSize?: string;
  logoWidth?: string;
  buttonText?: string;
  buttonLink?: string;
  hasQuoteMarks?: boolean;
  sourceStyles?: { fontSize: string; lineHeight: string; textAlign: "left" | "center" };
  index?: number;
  activeIndex?: number | null;
  setActiveIndex?: (index: number | null) => void;
}

export const TestimonialItem: types.Brick<TestimonialItemProps> = ({
  avatar,
  bigImg,
  logo,
  quote,
  source,
  vimeoId,
  videoURL,
  caseStudyURL,
  fontSize,
  logoWidth,
  buttonText,
  buttonLink,
  hasQuoteMarks = true,
  sourceStyles,
  index,
  activeIndex,
}) => {
  const isActive = index === activeIndex;

  // Only render if this is the active slide
  if (!isActive) return null;

  return (
    <>
      <QuoteWrap $bigImg={bigImg?.src}>
        <QuoteSlide
          logo={logo?.src}
          bigImg={bigImg?.src}
          quote={quote}
          source={source}
          logoWidth={logoWidth}
          hasQuoteMarks={hasQuoteMarks}
        />
      </QuoteWrap>
      <InfoSection $bigImg={bigImg?.src}>
        <QuoteSlideInfo
          avatar={avatar?.src}
          bigImg={bigImg?.src}
          logo={logo?.src}
          quote={quote}
          source={source}
          vimeoId={vimeoId}
          videoURL={videoURL}
          caseStudyURL={caseStudyURL}
          fontSize={fontSize}
          logoWidth={logoWidth}
          buttonText={buttonText}
          buttonLink={buttonLink}
          hasQuoteMarks={hasQuoteMarks}
          sourceStyles={sourceStyles}
          opacity={1}
          translateX={"0"}
          transitionSpeed={"400ms"}
        />
      </InfoSection>
    </>
  );
};

TestimonialItem.schema = {
  name: "testimonial-item",
  label: "Testimonial",
  category: "Testimonial Components",
  hideFromAddMenu: true,

  getDefaultProps: () => ({
    quote: "Quote goes here",
    source: "Source name",
    hasQuoteMarks: true,
  }),

  sideEditProps: [
    {
      name: "avatar",
      label: "Avatar Image URL",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Image,
      imageOptions: {
        maxWidth: 60,
      },
    },
    {
      name: "logo",
      label: "Logo URL",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Image,
      imageOptions: {
        maxWidth: 70,
      },
    },
    {
      name: "quote",
      label: "Quote Text",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Textarea,
    },
    {
      name: "source",
      label: "Source Name",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Text,
    },
    {
      name: "vimeoId",
      label: "Vimeo ID",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Text,
    },
    {
      name: "videoURL",
      label: "Video URL",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Text,
    },
    {
      name: "caseStudyURL",
      label: "Case Study URL",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Text,
    },
    {
      name: "buttonText",
      label: "Button Text",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Text,
    },
    {
      name: "buttonLink",
      label: "Button Link",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Text,
    },
    {
      name: "hasQuoteMarks",
      label: "Show Quote Marks",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Boolean,
    },
  ],
};

export default TestimonialItem;

const QuoteWrap = styled.div<{ $bigImg?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: ${(p) => (p.$bigImg ? "35rem" : "35rem")};
`;

const InfoSection = styled.div<{ $bigImg?: string }>`
  background: ${color.S3100};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: end;
  padding-bottom: 4.5rem;
  min-height: 10rem;

  ${tablet} {
    padding: 0 1rem 3rem;
    min-height: ${(p) => p.$bigImg && "7rem"};
  }
`;
